import * as theme from '../../../../theme';
import logo from '../../../../images/tb-white-main-word-only.png';
import logo2 from '../../../../images/tb-footer-gold.png';
import rhLogo from '../../../../images/rh-logo.png';
import rhQest from '../../../../images/rh-qest-device.jpeg';
import maregyOtf from '../../../../theme/MAREGY.otf';
import beautifullyOtf from '../../../../theme/Beautifully.otf';

// Import for label lookup (backwards compatible)
import { getScanLabel } from '../../../../contexts/FocusScanContext';

export function generateClientReportHTML(data, fullName, dateStr, brand = 'TruBalance') {
  const foodIntro = `When food or environmental allergens appear in your bioenergetic scan, it indicates a potential energetic imbalance or sensitivity your body may be responding to. This does not necessarily mean you have a clinical allergy, but rather that your body's energetic field may be reacting to these substances. These findings can offer valuable insight into possible stressors or irritants that could be impacting your overall wellness. They can help guide personalized lifestyle, dietary, or environmental adjustments to support your body's natural balance and vitality.`;

  const allergenIntro = `When food or environmental allergens appear in your bioenergetic scan, it indicates a potential energetic imbalance or sensitivity your body may be responding to. This does not necessarily mean you have a clinical allergy or positive IgE, but rather that your body's energetic field may be reacting to these substances. These findings can offer valuable insight into possible stressors or irritants that could be impacting your overall wellness. Use this information to guide personalized lifestyle, dietary, or environmental adjustments to support your body's natural balance and vitality.`;

  const foodCategoryOrder = [
    'beverages', 'dairy', 'fish', 'foodAdditives',
    'fruits', 'grains', 'legumes', 'meatAlternatives',
    'meat', 'nonDairyMilk', 'nutsSeeds', 'oilsFats',
    'phenolics', 'spicesHerbs', 'sugarsSweeteners', 'vegetables'
  ];

  const allergenCategoryOrder = [
    'additionalIngredients', 'airPollutants', 'animalDander', 'chemicals',
    'fossilFuels', 'metals', 'materials', 'pests',
    'phenolics', 'pollens', 'plants', 'harmfulEnergies'
  ];

  const displayTitles = {
    beverages: 'BEVERAGES',
    dairy: 'DAIRY',
    fish: 'FISH + SEAFOOD',
    foodAdditives: 'FOOD ADDITIVES',
    fruits: 'FRUITS',
    grains: 'GRAINS',
    legumes: 'LEGUMES',
    meatAlternatives: 'MEAT ALTERNATIVES',
    meat: 'MEAT + POULTRY',
    nonDairyMilk: 'NON-DAIRY MILK',
    nutsSeeds: 'NUTS + SEEDS',
    oilsFats: 'OILS + FATS',
    phenolics: 'PHENOLICS',
    spicesHerbs: 'SPICES + HERBS',
    sugarsSweeteners: 'SUGARS + SWEETENERS',
    vegetables: 'VEGETABLES',
    additionalIngredients: 'ADDITIONAL INGREDIENTS',
    airPollutants: 'AIR POLLUTANTS',
    animalDander: 'ANIMAL DANDER',
    chemicals: 'CHEMICALS',
    fossilFuels: 'FOSSIL FUELS',
    metals: 'METALS',
    materials: 'MATERIALS',
    pests: 'PESTS',
    pollens: 'POLLENS',
    plants: 'PLANTS',
    harmfulEnergies: 'HARMFUL ENERGIES'
  };

  const formatCategory = (key, items) => {
    const title = displayTitles[key] || key.replace(/([A-Z])/g, ' $1').trim().toUpperCase();
    let content = '';
    if (!items || items.length === 0) {
      content = `<p class="none">NONE</p>`;
    } else {
      content = `<ul class="item-list">${items.map((item) => `<li>${item}</li>`).join('')}</ul>`;
    }
    return `
      <div class="category-box">
        <h3 class="category-title">${title}</h3>
        ${content}
      </div>
    `;
  };

  const formatSensitivityPage = (sensitivityData, categoryOrder) => {
    return categoryOrder.map((key) => {
      if (sensitivityData.hasOwnProperty(key)) {
        return formatCategory(key, sensitivityData[key]);
      }
      return '';
    }).join('');
  };

  const foodSensitivity = data.foodSensitivity || {};
  const allergenSensitivity = data.allergenSensitivity || {};

  const foodGrid = formatSensitivityPage(foodSensitivity, foodCategoryOrder);
  const allergenGrid = formatSensitivityPage(allergenSensitivity, allergenCategoryOrder);

  const hasFoodItems = foodCategoryOrder.some(key => foodSensitivity[key] && foodSensitivity[key].length > 0);
  const hasAllergenItems = allergenCategoryOrder.some(key => allergenSensitivity[key] && allergenSensitivity[key].length > 0);

  // Toxin Burden Data
  const toxinBurden = data.toxinBurden || {};
  const toxinCategories = [
    'bacteria', 'chemical', 'emotional', 'food',
    'fungal', 'geopathic', 'heavyMetal', 'parasite',
    'mycoplasma', 'mycotoxin', 'viral', 'vaccine'
  ];

  const toxinDisplayTitles = {
    bacteria: 'BACTERIA',
    chemical: 'CHEMICAL',
    emotional: 'EMOTIONAL',
    food: 'FOOD',
    fungal: 'FUNGAL',
    geopathic: 'GEOPATHIC',
    heavyMetal: 'HEAVY METAL',
    parasite: 'PARASITE',
    mycoplasma: 'MYCOPLASMA',
    mycotoxin: 'MYCOTOXIN',
    viral: 'VIRAL',
    vaccine: 'VACCINE'
  };

  const toxinExplanations = {
    bacteria: 'Living organisms that enter cells and can reside within the tissues of our body',
    chemical: 'Come from the environment, food, cosmetics, clothing, cleaning products, etc.',
    emotional: 'Emotions stored within our tissues and cells can act like toxins to our body',
    food: 'Can be an intolerance, allergy, or due to gut imbalance. Gut imbalances can lead to excess histamines in response to various food items. Start by working on your gut.',
    fungal: 'Includes candida, mold, and other fungal sources. Excess processed sugar, dairy, alcohol, and improper food combining tend to create a fungal imbalance in the body.',
    geopathic: 'Natural electromagnetic field given off from the earth.',
    heavyMetal: 'Can come from the environment, food, cosmetics, clothing, cleaning products, etc.',
    parasite: 'Living organisms that we are often exposed to. Help decompose waste and if our body doesn’t clear them properly, they can reside within our gut and other tissues of the body.',
    mycoplasma: 'Microorganisms that are similar to parasites and bacteria, that are free living',
    mycotoxin: 'Mycotoxins are toxins that mold release, and molds can come from the environment, food (common in dairy, peanuts, alcohol, grains)',
    viral: 'Non-living organisms that invade cells and cause them to function improperly',
    vaccine: 'Often related to toxic by-products used in the vaccines. Can be from recent or past exposure (even passed down generationally)'
  };

  const hasToxinData = toxinCategories.some(key => (toxinBurden[key] || 0) > 0);

  const formatToxinBarChart = () => {
    return toxinCategories.map((key, index) => {
      const value = toxinBurden[key] || 0;
      const width = (value / 10) * 100 + '%';
      const title = toxinDisplayTitles[key] || key.toUpperCase();
      const barColors = [
        `${theme.Colors.RHBar1}`,
        `${theme.Colors.RHBar2}`,
        `${theme.Colors.RHBar3}`,
        `${theme.Colors.RHBar4}`
      ];
      const barColor = barColors[index % 4];
      return `
        <div class="bar-row">
          <div class="bar-label">${title}</div>
          <div class="bar-container">
            <div class="bar" style="width: ${width}; background-color: ${barColor};"></div>
          </div>
        </div>
      `;
    }).join('');
  };

  const formatToxinExplanations = () => {
    return toxinCategories.map((key) => {
      const title = toxinDisplayTitles[key] || key.toUpperCase();
      const explanation = toxinExplanations[key] || '';
      return `
        <div class="category-box larger-box">
          <h3 class="category-title">${title}</h3>
          <p class="category-text">${explanation}</p>
        </div>
      `;
    }).join('');
  };

  const toxinBurdenIntro = `This is not diagnostic, but shows which specific toxins are currently stressing your body energetically. Toxins can be from a past or current exposure. Your practitioner will utilize this information to help guide detox and supplement recommendations.`;

  // Page 1 Data Extraction
  const bioenergeticBalanceText = data.bioenergeticBalance || 'The QEST4 is a non-diagnostic tool that assesses for energetic imbalances within the body. When something stresses or weakens your body, it shows up in the system as an energetic imbalance. Through this assessment, we create a custom protocol, tailored to the specific needs of your body. Additionally, by taking your custom created imprint remedy, you will assist your body in working through the imbalances at an even deeper level!';
  const nutritionalSupportText = data.nutritionalSupport?.text || 'Nutrition is critical to feeling and performing our best because the body is only as healthy as the food we put in. This is why we recommend every client add in 1-2 superfood shakes/day into their regimen to see the best results!';
  const bestSuperfood = data.nutritionalSupport?.bestSuperfood || '';
  const dietSuggestion = data.nutritionalSupport?.dietSuggestion || '';
  const topPriorities = data.topPriorities || ['', '', '', ''];
  const topSupplements = data.topSupplements || ['', '', '', ''];
  const prioritiesIntro = 'These areas are most important to address and/or include in your wellness plan. The imprint remedy custom created and based on your scans to balance your body. Your individualized emotional balancing is included in the imprint remedy.';
  const supplementsIntro = 'These are listed in priority order based on what your body indicated. Your practitioner will work these into your protocol as needed to fit with your goals and budget.';

  const formatNumberedList = (items) => {
    return items.map((item, index) => `
      <div class="priority-item">
        <div class="priority-circle">${index + 1}</div>
        <div class="priority-text">${item}</div>
      </div>
    `).join('');
  };

  // Emotions Formatting
  const formatEmotions = (emotionsData) => {
    if (!emotionsData) return { main: '<p class="secondary-text">No summary available</p>', additional: '' };

    if (typeof emotionsData === 'string') {
      const content = emotionsData.split('\n').map((line) => {
        if (line.trim().endsWith(':')) {
          return `<div class="emotion-heading">${line}</div>`;
        } else {
          return `<p class="emotions-text">${line}</p>`;
        }
      }).join('');
      return { main: content, additional: '' };
    }

    let mainSections = '';
    let additionalSections = '';

    const addSection = (title, content) => {
      mainSections += `
        <div class="emotion-heading">${title}:</div>
        ${content}
      `;
    };

    const primaryDisharmony = emotionsData.primaryDisharmony || 'None';
    if (primaryDisharmony !== 'None') {
      addSection('Primary Emotional Disharmony', `<p class="emotions-text">${primaryDisharmony}</p>`);
    }

    if (emotionsData.factorsConnected?.length > 0) {
      const factorsContent = `
        <div class="emotion-list">
          ${emotionsData.factorsConnected.map(factor => `<p class="emotions-text"> - ${factor}</p>`).join('')}
        </div>
      `;
      addSection('Factors Connected to Emotional Disharmony', factorsContent);
    }

    const bachFlowerBalance = emotionsData.bachFlowerBalance || [];
    if (bachFlowerBalance.length > 0) {
      const bachContent = bachFlowerBalance.map((flower) => {
        return `<p class="emotions-text flower-item"><span style="font-weight: 500; font-style: italic;">${flower.item}:</span> ${flower.description}</p>`;
      }).join('');
      addSection('Bach Flower Balance', bachContent);
    }

    if (emotionsData.emotionsAffecting?.length > 0 && emotionsData.emotionsAffecting[0] !== 'None') {
      const affectingContent = `
        <div class="emotion-list">
          ${emotionsData.emotionsAffecting.map(emotion => `<p class="emotions-text">${emotion}</p>`).join('')}
        </div>
      `;
      addSection('Body Systems Affected by Emotions', affectingContent);
    }

    if (emotionsData.bodyStressors?.length > 0) {
      const stressorsContent = `
        <div class="emotion-list">
          ${emotionsData.bodyStressors.map(stressor => `<p class="emotions-text">${stressor}</p>`).join('')}
        </div>
      `;
      addSection('Body Stressors', stressorsContent);
    }

    const words = emotionsData.wordsOfAffirmation?.words || [];
    const description = emotionsData.wordsOfAffirmation?.description || '';
    if (words.length > 0 || description) {
      addSection('Words of Affirmation', `<p class="emotions-text">${words.join(', ')} - ${description}</p>`);
    }

    if (emotionsData.limitingBeliefs?.length > 0) {
      const beliefsContent = `
        <div class="emotion-list">
          ${emotionsData.limitingBeliefs.map(belief => `<p class="emotions-text"> - ${belief}</p>`).join('')}
        </div>
      `;
      addSection('Limiting Beliefs', beliefsContent);
    }

    const soulFilters = emotionsData.soulFilters || 'None';
    if (soulFilters !== 'None') {
      addSection('Soul Filters', `<p class="emotions-text">${soulFilters}</p>`);
    }

    if (emotionsData.emotionalImbalances?.length > 0) {
      additionalSections += `
        <div class="additional-header">
          ADDITIONAL EMOTIONAL IMBALANCES
        </div>
      `;

      const items = emotionsData.emotionalImbalances;
      const columns = [[], [], []];
      items.forEach((item, index) => {
        columns[index % 3].push(item);
      });

      additionalSections += `<div style="display: flex; justify-content: space-around; margin-top: 15px; padding-left: 15px; padding-right: 15px;">`;
      columns.forEach(col => {
        additionalSections += `<ul style="list-style-type: none; padding: 0; margin: 0; width: 30%;">`;
        col.forEach(item => {
          additionalSections += `<li>- ${item}</li>`;
        });
        additionalSections += `</ul>`;
      });
      additionalSections += `</div>`;
    }

    return { main: mainSections || '<p class="secondary-text">No summary available</p>', additional: additionalSections };
  };

  const { main: emotionsMain, additional: emotionsAdditional } = formatEmotions(data.emotions || {});
  const hasEmotionsData = !emotionsMain.includes('No summary available') || emotionsAdditional !== '';

  // Your Supplements
  const yourSupplements = data.yourSupplements || [];
  const hasSupplements = yourSupplements.length > 0;
  const yourSupplementsIntro = 'These are the supplements you and/or your practitioner wanted tested.';
  const supplementsNote = 'Please note: If the dose is significantly higher than the recommended amount it can either mean you are extremely deficient in that item, or you are not absorbing this specific supplement well. If the dose is lower than recommended, you just may not need the full dose.';
  const prescriptionNote = 'For prescription meds: even if they do not test well. You are never to stop meds without consulting your MD. We do not make any claims or recommendations surrounding medications, other that what your body likes or doesn\'t like energetically.';

  const formatSupplementsList = () => {
    if (yourSupplements.length === 0) {
      return '<p class="secondary-text">None</p>';
    }
    return formatNumberedList(yourSupplements);
  };

  // ==================== FOCUS SCANS - UPDATED ====================
  const focusScans = data.focusScans || [];
  const supportiveTherapies = data.supportiveTherapies || [];
  const additionalSuggestions = data.additionalSuggestions || [];

  const formatFocusScan = (scanObj) => {
    const { scan, items = [], comment = '' } = scanObj;

    const itemsHTML = items.map(it => {
      const itemName = it.item || 'Unnamed Item';

      if (it.description !== undefined) {
        const desc = (it.description || '').trim();
        if (desc) {
          // Preserve line returns from the editor as line breaks in the printed report
          const descHTML = desc.replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
          return `<p class="focus-item"><strong>${itemName}:</strong> ${descHTML}</p>`;
        } else {
          // No description → just bold item, no colon, no extra text
          return `<p class="focus-item"><strong>${itemName}</strong></p>`;
        }
      } 
      else if (it.items && Array.isArray(it.items)) {
        if (it.items.length > 0) {
          const sublist = it.items.map(sub => `<li>${sub}</li>`).join('');
          return `<p class="focus-item"><strong>${itemName}:</strong></p><ul class="focus-sublist">${sublist}</ul>`;
        } else {
          // No sub-items → just bold item, no colon, no extra text
          return `<p class="focus-item"><strong>${itemName}</strong></p>`;
        }
      }
      return '';
    }).join('');

    const commentHTML = comment ? `<p class="focus-comment">${comment}</p>` : '';

    // Use nice label for header (backwards compatible)
    const displayLabel = getScanLabel(scan) || scan || '';

    return `
      <div class="focus-box">
        <div class="focus-title">${displayLabel.toUpperCase()}</div>
        <div class="focus-items">
          ${itemsHTML}
          ${commentHTML}
        </div>
      </div>
    `;
  };

  const focusHTMLs = focusScans.map(formatFocusScan);
  const numFocus = focusScans.length;

  const supportiveHTML = supportiveTherapies.length > 0 ? `
    <div class="support-box">
      <div class="support-title">SUPPORTIVE THERAPIES</div>
      <div class="focus-items">
        <ul class="support-list">
          ${supportiveTherapies.map(th => `<li>${th}</li>`).join('')}
        </ul>
      </div>
    </div>
  ` : '';

  const additionalHTML = additionalSuggestions.length > 0 ? `
    <div class="additional-box">
      <div class="additional-title">ADDITIONAL SUGGESTIONS</div>
      <div class="focus-items">
        ${additionalSuggestions.map(sug => {
          const descText = (sug.description || '').trim();
          const colon = descText ? ':' : '';
          return `<p class="additional-item"><strong>${sug.item}${colon}</strong> ${descText}</p>`;
        }).join('')}
      </div>
    </div>
  ` : '';

  let pages = [];
  let i = 0;
  while (i < numFocus) {
    let pageContent = focusHTMLs[i] || '';
    i++;
    if (i < numFocus) {
      pageContent += focusHTMLs[i] || '';
      i++;
    }
    pages.push(pageContent);
  }

  const extraHTML = supportiveHTML + additionalHTML;
  if (numFocus > 0) {
    if (numFocus % 2 === 1) {
      if (pages.length > 0 && extraHTML) {
        pages[pages.length - 1] += extraHTML;
      }
    } else {
      if (extraHTML) {
        pages.push(extraHTML);
      }
    }
  } else {
    if (extraHTML) {
      pages.push(extraHTML);
    }
  }

  pages = pages.filter(p => p.trim() !== '');

  let focusSection = '';
  pages.forEach((pageContent) => {
    focusSection += `
      <div class="page-header">
        <div class="header-container">
          <div class="header-left">
          </div>
          <div class="header-right">
            <img src="${logo2}" alt="Logo" class="logo2">
          </div>
        </div>
      </div>
      <div class="container">
        <div class="full-width">
          ${pageContent}
        </div>
      </div>
    `;
  });

  const fontUrl = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap';
  const importRule = `@import url('${fontUrl}');`;

  const fontFaceRule = `
    @font-face {
      font-family: 'Maregy';
      src: url('${maregyOtf}') format('opentype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;

  const fontFaceBeautifully = `
    @font-face {
      font-family: 'Beautifully';
      src: url('${beautifullyOtf}') format('opentype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `;

  const logoSrc = brand === 'TruBalance' ? logo : rhLogo;
  const logoSrc2 = brand === 'TruBalance' ? logo2 : rhLogo;
  const purpleColor = brand === 'TruBalance' ? theme.Colors.Purple : theme.Colors.RHPurple;
  const purpleLight = brand === 'TruBalance' ? theme.Colors.PurpleLight : theme.Colors.RHPurpleLight;
  const grayDark = brand === 'TruBalance' ? theme.Colors.GrayDark : theme.Colors.RHBlack;
  const logoMaxHeight = brand === 'TruBalance' ? '14px' : '26px';
  const logo2MaxHeight = brand === 'TruBalance' ? '90px' : '40px';
  const logo2Right = brand === 'TruBalance' ? '10px' : '25px';
  const logoTop = brand === 'TruBalance' ? '100px' : '96px';
  const logoLeft = brand === 'TruBalance' ? '238px' : '207px';
  const logoTopPrint = brand === 'TruBalance' ? '98px' : '93px';
  const logoLeftPrint = brand === 'TruBalance' ? '242px' : '216px';
  const logoMaxHeightPrint = brand === 'TruBalance' ? '10.5px' : '20px';

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Client Report</title>
      <style>
        ${importRule}
        ${fontFaceRule}
        ${fontFaceBeautifully}
        body { font-family: Montserrat, Arial, sans-serif; background: white; margin: 0; padding: 0; }
        .container { max-width: 800px; margin: 0 auto; padding: 10px; }
        .full-width { width: 100%; }
        .header-bar { background-color: ${grayDark}; height: auto; width: 100%; display: flex; justify-content: center; align-items: center; padding: 20px; box-sizing: border-box; }
        .page-header { background-color: ${grayDark}; height: 98px; width: 100%; display: flex; justify-content: center; align-items: center; padding: 20px; box-sizing: border-box; }
        .header-container { display: flex; justify-content: space-between; align-items: center; max-width: 800px; width: 100%; height: 120px; position: relative; }
        .header-left { flex: 2; display: flex; flex-direction: column; align-items: flex-start; }
        .header-title { }
        .header-title.big { font-size: 120px; top: -20px; }
        .header-title.small { font-size: 80px; top: 0px; }
        .header-title.first { font-family: Beautifully, Montserrat, Arial; font-size: 90px; color: ${theme.Colors.Gold}; position: absolute; top: -20px; left: 10px; }
        .header-title.second { font-family: Maregy, Montserrat, Arial; font-size: 47px; color: white; text-transform: uppercase; position: absolute; top: 55px; left: 165px; }
        .logo { max-height: ${logoMaxHeight}; position: absolute; top: ${logoTop}; left: ${logoLeft}; }
        .logo2 { max-height: ${logo2MaxHeight}; padding-right: ${logo2Right}; }
        .header-right { flex: 1; display: flex; flex-direction: column; align-items: flex-end; color: white; font-size: 16px; }
        .header-detail-container { margin-top: 15px; text-transform: uppercase; display: flex; flex-direction: row; width: 100%; justify-content: space-between; }
        .header-detail-left { flex: 0.75 }
        .header-detail-right { flex: 2; }
        .sensitivity-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-top: 20px; margin-bottom: 20px; }
        .category-box { background-color: #f5f5f5; padding: 10px; border-radius: 5px; height: 165px; text-align: center; }
        .larger-box { height: 170px; }
        .category-title { text-transform: uppercase; font-weight: bold; font-size: 14px; margin-bottom: 5px; }
        .category-text { font-size: 12px; line-height: 1.3 }
        .item-list { list-style-type: none; padding: 0; margin: 0; margin-top: 14px; }
        .item-list li { font-size: 12px; margin-bottom: 2px; }
        .none { font-size: 12px; margin: 0; }
        .print-btn { position: absolute; top: 20px; right: 20px; padding: 8px 16px; background: ${purpleColor}; border-radius: 4px; color: white; border: none; cursor: pointer; }
        .print-btn:hover { background: ${theme.Colors.GoldDark}; }
        .header-text { font-size: 26px; font-family: Maregy; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1.2px; -webkit-text-stroke: 1px black; }
        .intro-text { font-size: 12px; margin-bottom: 15px; text-align: justify; }
        .secondary-text { font-size: 13px; margin-bottom: 15px; text-align: justify; line-height: 1.3 }
        .toxin-upper { display: flex; justify-content: space-between; margin-bottom: 20px; margin-top: 20px; }
        .toxin-left { flex: 0.9; padding-right: 45px; display: flex; justify-content: center; flex-direction: column; }
        .toxin-right { flex: 1.1; }
        .bar-chart { display: flex; flex-direction: column; gap: 3px; }
        .bar-row { display: flex; align-items: center; }
        .bar-label { width: 120px; text-align: right; padding-right: 10px; font-size: 12.4px; text-transform: uppercase; font-weight: bold; }
        .bar-container { flex: 1; height: 17px; }
        .bar { height: 100%; opacity: 0.65; border-top-right-radius: 4px; border-bottom-right-radius: 4px; }
        .bio-section { display: flex; gap: 40px; margin-top: 10px; margin-bottom: 20px; margin-left: 20px; margin-right: 20px; }
        .bio-text { flex: 1.2; }
        .bio-image { flex: 1; display: flex; align-items: center; justify-content: center; }
        .bio-image img { max-width: 100%; height: auto; }
        .nutritional-box { background-color: #f5f5f5; padding: 30px; margin-bottom: 20px; }
        .nutritional-box .header-text { margin: 0px; margin-bottom: 8px; }
        .nutritional-box .secondary-text { margin: 0px; margin-bottom: 12px; }
        .top-section { display: flex; gap: 20px; margin-bottom: 20px; }
        .top-box { flex: 1; border: 6px double ${purpleColor}; padding: 25px; display: flex; flex-direction: column; }
        .top-box h3 { font-size: 20px; font-weight: normal; font-family: Maregy; text-transform: uppercase; letter-spacing: 1.2px; -webkit-text-stroke: 1px black; text-align: center; margin-bottom: 10px; margin-top: 0px; }
        .priorities-list { display: flex; flex-direction: column; gap: 10px; margin-top: auto; }
        .priority-item { display: flex; align-items: center; gap: 10px; }
        .priority-circle { color: ${purpleColor}; width: 25px; height: 25px; border-radius: 50%; border: 4px double ${purpleColor}; display: flex; justify-content: center; align-items: center; font-size: 15px; flex-shrink: 0; }
        .priority-text { text-transform: uppercase; font-size: 14px; }
        .emotions-wrapper { position: relative; padding-bottom: 50px; }
        .emotions-container { padding-left: 20px; padding-right: 20px; box-sizing: border-box; }
        .emotion-heading { font-size: 16px; font-weight: bold; text-transform: uppercase; margin-top: 20px; margin-bottom: 8px; }
        .emotion-heading + .secondary-text { margin-top: 0; }
        .emotions-text { font-size: 14px; line-height: 1.3; margin-bottom: 0px; margin-top: 0px; }
        .emotion-list .secondary-text { margin-top: 10px; margin-bottom: 0; }
        .emotion-list .secondary-text:first-child { margin-top: 0; }
        .emotion-inline { display: flex; align-items: baseline; }
        .emotion-inline-text { font-size: 14px; font-weight: normal; text-transform: none; margin-left: 5px; }
        .additional { font-size: 14px; margin-top: auto; page-break-inside: avoid; }
        .additional-header { font-size: 14px; box-sizing: border-box; background-color: ${purpleColor}; height: 30px; width: 100%; display: flex; align-items: center; justify-content: flex-start; padding-left: 30px; color: white; font-weight: bold; text-transform: uppercase; margin-top: 20px; }
        .flower-item { margin-top: 7px; }
        .purple-bar { background-color: ${purpleColor}; height: 12px; width: 100%; margin-top: 10px; }
        .supplements-box { border: 6px double ${purpleColor}; padding: 25px; margin-bottom: 50px; display: flex; flex-direction: column; margin-top: 50px;  }
        .supplements-box .header-text { margin: 0px; margin-bottom: 8px; }
        .supplements-box .secondary-text { margin: 0px; margin-bottom: 12px; }
        .supplements-box .priorities-list { margin-top: 20px; }
        .focus-box, .support-box, .additional-box { margin-bottom: 20px; border: 1px solid #000000; margin-top: 20px; width: 100%; }
        .focus-title { background-color: #e0e0e0; }
        .support-title, .additional-title { background-color: ${purpleLight}; }
        .focus-title, .support-title, .additional-title { font-family: 'Maregy'; padding: 13px; text-transform: uppercase; font-size: 20px; letter-spacing: 1.2px; -webkit-text-stroke: 1px black; border-bottom: 1px solid #000000; padding-bottom: 11px; }
        .focus-items, .additional-items, .support-items { padding: 20px; }
        .focus-items p:first-of-type ~ p { margin: 20px 0 5px 0; }
        .focus-items p, .additional-item { font-size: 14px; line-height: 1.3; margin: 5px 0 5px 0; }
        .focus-sublist, .support-list { list-style-type: disc; padding-left: 20px; margin: 0; font-size: 14px; line-height: 1.3; }
        .support-list li:first-of-type ~ li { margin: 13px 0 0 0 }
        .focus-comment { font-size: 14px; line-height: 1.3; margin: 20px 0 0 0; }
        @media print { 
          .print-btn { display: none; } 
          @page { margin: 0; size: letter portrait;}
          body { margin: 0; padding: 0; display: flex; flex-direction: column; height: 100%; }
          .container { max-width: 100%; width: 100%; height: auto; flex: 1; box-sizing: border-box; padding: 10px 40px; }
          .header-bar { justify-content: space-between; padding: 15px 10px; flex-shrink: 0; page-break-after: avoid; }
          .page-header { background-color: ${grayDark}; height: 98px; width: 100%; align-items: center; padding: 20px; box-sizing: border-box; display: flex; justify-content: space-between; padding: 15px 10px; flex-shrink: 0; page-break-before: always; page-break-after: avoid; }
          .header-container { max-width: 100% }
          .header-title { }
          .header-title.big { font-size: 100px; top: -10px; }
          .header-title.small { font-size: 65px; top: 15px; }
          .header-title.first { font-size: 78px; top: -2px; left: 50px; }
          .header-title.second { font-size: 36px; top: 62px; left: 185px; }
          .header-detail-left { flex: 0.6 }
          .logo { max-height: ${logoMaxHeightPrint}; top: ${logoTopPrint}; left: ${logoLeftPrint}; }
          .logo2 { top: 10px; position: relative; }
          .header-right { font-size: 12px; }
          .sensitivity-grid { gap: 20px; }
          .category-title { font-size: 12px; }
          .category-text { font-size: 11.2px; line-height: 1.2 }
          .item-list li { font-size: 10px; }
          .none { font-size: 10px; }
          .header-text { font-size: 20px; margin-bottom: 6px; }
          .intro-text { font-size: 9.7px; line-height: 1.1 }
          .secondary-text { font-size: 13px; line-height: 1.3 }
          .bar-label { font-size: 11px; width: 100px; }
          .emotions-wrapper { height: 1056px; padding-bottom: 0px; }
          .emotion-heading { font-size: 12px; }
          .emotions-text { font-size: 12px; }
          .emotion-inline-text { font-size: 12px; }
          .priority-text { font-size: 11px; }
          .priority-circle { width: 22px; height: 22px; border: 3px double ${purpleColor}; font-size: 10px; }
          .purple-bar { page-break-inside: avoid; }
          .additional-header { font-size: 12px; }
          .additional { font-size: 12px; position: absolute; bottom: 40px; left: 20px; width: calc(100% - 40px); }
          .focus-title, .support-title, .additional-title { font-size: 12px; }
          .focus-items p, .additional-item { font-size: 11px; }
          .focus-sublist li, .support-list li { font-size: 11px; }
          .focus-comment { font-size: 11px; }
          * { box-sizing: border-box; orphans: 1; widows: 1; }
        }
      </style>
    </head>
    <body>
      <div class="header-bar">
        <div class="header-container">
          <div class="header-left">
            <div class="header-title big first">Bioenergetic</div>
            <div class="header-title big second">Analysis</div>
            <img src="${logoSrc}" alt="Logo" class="logo">
          </div>
          <div class="header-right">
            <div class="header-detail-container"><div class="header-detail-left">Name&nbsp;&nbsp;|</div><div class="header-detail-right">${fullName}</div></div>
            <div class="header-detail-container"><div class="header-detail-left">Date&nbsp;&nbsp;|</div><div class="header-detail-right">${dateStr}</div></div>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="full-width">
          <div class="bio-section">
            <div class="bio-text">
              <p class="header-text">Bioenergetic Balance</p>
              <p class="secondary-text">${bioenergeticBalanceText}</p>
            </div>
            <div class="bio-image">
              <img src="${rhQest}" alt="Bioenergetic Image">
            </div>
          </div>
          <div class="nutritional-box">
            <p class="header-text">Nutritional Support</p>
            <p class="secondary-text">${nutritionalSupportText}</p>
            <p class="secondary-text"><strong>Best Superfood</strong> - ${bestSuperfood}</p>
            <p class="secondary-text" style="margin-bottom: 0px;"><strong>Diet Suggestion</strong> - ${dietSuggestion}</p>
          </div>
          <div class="top-section">
            <div class="top-box">
              <h3>Top 4 Priorities</h3>
              <p class="secondary-text">${prioritiesIntro}</p>
              <div class="priorities-list">
                ${formatNumberedList(topPriorities)}
              </div>
            </div>
            <div class="top-box">
              <h3>Top 4 Supplements</h3>
              <p class="secondary-text">${supplementsIntro}</p>
              <div class="priorities-list">
                ${formatNumberedList(topSupplements)}
              </div>
            </div>
          </div>
        </div>
      </div>
  `;

  if (hasToxinData) {
    html += `
      <div class="page-header">
        <div class="header-container">
          <div class="header-left">
          </div>
          <div class="header-right">
            <img src="${logoSrc2}" alt="Logo" class="logo2">
          </div>
        </div>
      </div>
      <div class="container">
        <div class="full-width">
          <div class="toxin-upper">
            <div class="toxin-left">
              <p class="header-text">Toxin Burden Priorities</p>
              <p class="secondary-text">${toxinBurdenIntro}</p>
            </div>
            <div class="toxin-right">
              <div class="bar-chart">
                ${formatToxinBarChart()}
              </div>
            </div>
          </div>
          <p class="header-text">Toxin Explanations</p>
          <div class="sensitivity-grid">
            ${formatToxinExplanations()}
          </div>
        </div>
      </div>
    `;
  }

  if (hasEmotionsData) {
    html += `
      <div class="emotions-wrapper">
        <div class="page-header">
          <div class="header-container">
            <div class="header-left">
            </div>
            <div class="header-right">
              <img src="${logoSrc2}" alt="Logo" class="logo2">
            </div>
          </div>
        </div>
        <div class="container">
          <div class="full-width emotions-container">
            <p class="header-text">Emotional Imbalances</p>
            <p class="secondary-text emotion-heading-text">Emotions often underlie bodily imbalances. This scan summary highlights key emotional disturbances, where your body feels "stuck" in the states listed below. These may arise from physical traumas (retaining emotional imprints), generational inheritance (via cells), or absorbed from close relationships.</p>
            <p class="secondary-text emotion-heading-text">Bach flower essences are plant-based remedies that promote emotional harmony. You can purchase the recommended essence or let your imprint remedy carry its energetic signature for support.</p>
            <p class="secondary-text emotion-heading-text">By using your personalized imprint remedy, you encourage your body to work through these emotional blocks.</p>
          </div>
          <div class="purple-bar"></div>
          <div class="full-width emotions-container">
            <div class="content main-content-wrapper" style="margin-top: 20px;">
              ${emotionsMain}
            </div>
          </div>
          <div class="full-width">
            <div class="additional">
              ${emotionsAdditional}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  if (focusSection) {
    html += focusSection;
  }

  if (hasSupplements) {
    html += `
      <div class="page-header">
        <div class="header-container">
          <div class="header-left">
          </div>
          <div class="header-right">
            <img src="${logoSrc2}" alt="Logo" class="logo2">
          </div>
        </div>
      </div>
      <div class="container">
        <div class="full-width">
          <div class="supplements-box">
            <p class="header-text">Your Supplements</p>
            <p class="secondary-text">${yourSupplementsIntro}</p>
            <p class="secondary-text">${supplementsNote}</p>
            <p class="secondary-text" style="margin-bottom: 0px;">${prescriptionNote}</p>
            <div class="priorities-list">
              ${formatSupplementsList()}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  if (hasFoodItems) {
    html += `
      <div class="page-header">
        <div class="header-container">
          <div class="header-left">
          </div>
          <div class="header-right">
            <img src="${logoSrc2}" alt="Logo" class="logo2">
          </div>
        </div>
      </div>
      <div class="container">
        <div class="full-width">
          <p class="header-text">Food Sensitivity</p>
          <p class="secondary-text">${foodIntro}</p>
          <div class="sensitivity-grid">
            ${foodGrid}
          </div>
        </div>
      </div>
    `;
  }

  if (hasAllergenItems) {
    html += `
      <div class="page-header">
        <div class="header-container">
          <div class="header-left">
          </div>
          <div class="header-right">
            <img src="${logoSrc2}" alt="Logo" class="logo2">
          </div>
        </div>
      </div>
      <div class="container">
        <div class="full-width">
          <p class="header-text">Environmental Allergen Sensitivity</p>
          <p class="secondary-text">${allergenIntro}</p>
          <div class="sensitivity-grid">
            ${allergenGrid}
          </div>
        </div>
      </div>
    `;
  }

  html += `
      <button class="print-btn" onclick="window.print()">Print</button>
    </body>
    </html>
  `;

  return html;
}
